import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  format,
  startOfMonth,
  addMonths,
  lastDayOfMonth,
  isAfter,
  parse,
  differenceInDays,
} from 'date-fns';
import TableRowComponent from '../components/TableRowComponent';
import {
  firstDateOfQuarter,
  getDateFromString,
  getMatchedMonth,
  getQuarterEndDates,
  isCurrentMonth,
  isPartialMatch,
} from '../../../utils/helpers';
import rowsForQuarterlyData from '../constants/rowsForQuarterlyData';
import rowsForMonthlyData from '../constants/rowsForMonthlyData';
import { filterType } from '../../../constants/filterOptions';

function Last12MonthsContainer({ companySelected, startValue, endValue, payload={}, selectedFilter }) {
  const paidRegisteredUsers = useSelector((state) => state.kpi.last12Months.paidRegisteredUsers);
  const activeUsers = useSelector((state) => state.kpi.last12Months.activeUsers);
  const totalUsersPlayedContent = useSelector(
    (state) => state.kpi.last12Months.totalUsersPlayedContent
  );
  const totalMinsPlayed = useSelector((state) => state.kpi.last12Months.totalMinsPlayed);
  const usersRatedTheContent = useSelector((state) => state.kpi.last12Months.usersRatedTheContent);
  const usersFavoriteTheContent = useSelector(
    (state) => state.kpi.last12Months.usersFavoriteTheContent
  );
  const totalStepsCaptured = useSelector((state) => state.kpi.last12Months.totalStepsCaptured);
  const totalUsersSyncingSteps = useSelector(
    (state) => state.kpi.last12Months.totalUsersSyncingSteps
  );
  const numberOfPcCompleted = useSelector((state) => state.kpi.last12Months.numberOfPcCompleted);
  const numberOfPcBookings = useSelector((state) => state.kpi.last12Months.numberOfPcBookings);
  const numberOfUsersTakingWaterIntake = useSelector(
    (state) => state.kpi.last12Months.numberOfUsersTakingWaterIntake
  );
  const totalEarnedRewardPoints = useSelector(
    (state) => state.kpi.last12Months.totalEarnedRewardPoints
  );
  const totalRedeemedRewardPoints = useSelector(
    (state) => state.kpi.last12Months.totalRedeemedRewardPoints
  );
  const totalUsersEarnedReward = useSelector(
    (state) => state.kpi.last12Months.totalUsersEarnedReward
  );
  const totalUsersRedeemedReward = useSelector(
    (state) => state.kpi.last12Months.totalUsersRedeemedReward
  );
  const totalPushSent = useSelector((state) => state.kpi.last12Months.totalPushSent);
  const totalChallengesLaunched = useSelector(
    (state) => state.kpi.last12Months.totalChallengesLaunched
  );
  const totalUsersJoinedInChallenge = useSelector(
    (state) => state.kpi.last12Months.totalUsersJoinedInChallenge
  );
  const totalUsersParticipatedInChallenge = useSelector(
    (state) => state.kpi.last12Months.totalUsersParticipatedInChallenge
  );
  const programJoined = useSelector((state) => state.kpi.last12Months.programJoined);
  const programCompleted = useSelector((state) => state.kpi.last12Months.programCompleted);
  const bookRead = useSelector((state) => state.kpi.last12Months.bookRead);
  const totalMealImagesUploaded = useSelector(
    (state) => state.kpi.last12Months?.totalMealImagesUploaded
  );
  const totalAiCoachMessages = useSelector(
    (state) => state.kpi.last12Months?.totalAICoachMessages
  );
  const uniqueNutritionUsers = useSelector(
    (state) => state.kpi.last12Months?.uniqueNutritionUsers
  );
  const totalMealsAdded = useSelector(
    (state) => state.kpi.last12Months?.totalMealsAdded
  );
  const [showQuarterly, setShowQuarterly] = useState(false);
  const navigate = useNavigate();

  function getMonthsInRange(startDate, endDate) {
    const months = [];
    let currentDate = new Date(startDate);
    let currentEndDate = new Date(endDate);
    while (
      isAfter(currentEndDate, currentDate) ||
      currentDate.getMonth() === currentEndDate.getMonth()
    ) {
      months.push(format(lastDayOfMonth(currentDate), 'yyyy-MM-dd'));
      currentDate = addMonths(currentDate, 1);
    }
    return months;
  }

  function getAvgUsePerDay(month, quarter, quarterly = false) {
    const avgMinsPlayedPerDay = getAvgMinutesPlayedPerDay(month, quarter, quarterly);
    const stepsToMins = getStepsToMinsConversion(month, quarter, quarterly);
    const avgWaterIntakePerUserPerDay = getAvgWaterIntakePerUserPerDay(month, quarter, quarterly);

    let sum = 0;

    if (!isNaN(avgMinsPlayedPerDay)) {
      sum += Number(avgMinsPlayedPerDay);
    }
    if (!isNaN(stepsToMins)) {
      sum += Number(stepsToMins);
    }
    if (!isNaN(avgWaterIntakePerUserPerDay)) {
      sum += Number(avgWaterIntakePerUserPerDay);
    }
    return sum.toFixed(2);
  }

  function getActiveUsersPercentageForMonth(month) {
    if (
      activeUsers.loading &&
      ((new Date(month) >= new Date(payload.startDate) &&
        new Date(month) <= new Date(payload.endDate)) ||
        isPartialMatch(month, payload.endDate))
    ) {
      return 'loading';
    }
    if (!paidRegisteredUsers.data || !activeUsers.data) {
      return 0;
    }
    const matchedMonth1 = getMatchedMonth(paidRegisteredUsers.data, month, startValue, endValue);
    const matchedMonth2 = getMatchedMonth(activeUsers.data, month, startValue, endValue);
    const totalUsers = paidRegisteredUsers.data[matchedMonth1]?.registered_users;
    const realUsers = activeUsers.data[matchedMonth2]?.activeUsers;
    if (totalUsers && realUsers) {
      return ((realUsers * 100) / totalUsers).toFixed(2);
    }
    return 0;
  }

  function getAvgMinutesPlayedPerDay(month, quarter, quarterly = false) {
    if (
      totalMinsPlayed.loading &&
      ((new Date(month) >= new Date(payload.startDate) &&
        new Date(month) <= new Date(payload.endDate)) ||
        isPartialMatch(month, payload.endDate))
    ) {
      return 'loading';
    }
    if (!totalMinsPlayed.data || !activeUsers.data) {
      return 0;
    }
    let totalMins;
    let realActiveUsers;
    let totalDays;

    if (quarterly) {
      totalMins =
        Object.values(totalMinsPlayed.data).find((item) => item.quarter === quarter.quarterNo)
          ?.total_mins ?? 0;
      realActiveUsers =
        Object.values(activeUsers.data).find((item) => item.quarter === quarter.quarterNo)
          ?.activeUsers ?? 0;

      const qStartDate = parse(quarter.startDate, 'yyyy-MM-dd', new Date());
      const qEndDate = parse(quarter.endDate, 'yyyy-MM-dd', new Date());
      totalDays = differenceInDays(qEndDate, qStartDate);
    } else {
      const matchedMonth1 = getMatchedMonth(totalMinsPlayed.data, month, startValue, endValue);
      const matchedMonth2 = getMatchedMonth(activeUsers.data, month, startValue, endValue);

      if (matchedMonth1 && matchedMonth2) {
        totalDays = Math.min(new Date(matchedMonth1).getDate(), new Date(matchedMonth2).getDate());
        totalMins = totalMinsPlayed.data[matchedMonth1]?.total_mins;
        realActiveUsers = activeUsers.data[matchedMonth2]?.activeUsers;
      }
    }
    if (isNaN(totalMins) || isNaN(realActiveUsers) || isNaN(totalDays)) {
      return 0;
    }
    const result = totalMins / (realActiveUsers * totalDays);
    return result?.toFixed(2) || 0;
  }

  function getUsersPercentageRatedTheContent(month) {
    if (
      usersRatedTheContent.loading &&
      ((new Date(month) >= new Date(payload.startDate) &&
        new Date(month) <= new Date(payload.endDate)) ||
        isPartialMatch(month, payload.endDate))
    ) {
      return 'loading';
    }
    if (!usersRatedTheContent.data || !totalUsersPlayedContent.data) {
      return 0;
    }
    const matchedMonth1 = getMatchedMonth(usersRatedTheContent.data, month, startValue, endValue);
    const matchedMonth2 = getMatchedMonth(totalUsersPlayedContent.data, month, startValue, endValue);
    const totalUsersRated = usersRatedTheContent.data[matchedMonth1]?.total_users_rated_content;
    const totalUsersPlayed =
      totalUsersPlayedContent.data[matchedMonth2]?.total_users_played_content;

    if (totalUsersRated && totalUsersPlayed) {
      return ((totalUsersRated * 100) / totalUsersPlayed).toFixed(2);
    }
    return 0;
  }

  function getAvgStepsPerUserPerDay(month, quarter, quarterly = false) {
    if (
      totalUsersSyncingSteps.loading &&
      ((new Date(month) >= new Date(payload.startDate) &&
        new Date(month) <= new Date(payload.endDate)) ||
        isPartialMatch(month, payload.endDate))
    ) {
      return 'loading';
    }
    if (!totalStepsCaptured.data || !totalUsersSyncingSteps.data) {
      return 0;
    }
    let totalSteps = 0;
    let totalUsers = 0;
    let totalDays = 0;
    if (quarterly) {
      totalSteps =
        Object.values(totalStepsCaptured.data).find((item) => item.quarter === quarter.quarterNo)
          ?.total_steps ?? 0;
      totalUsers =
        Object.values(totalUsersSyncingSteps.data).find(
          (item) => item.quarter === quarter.quarterNo
        )?.total_users_syncing_steps ?? 0;

      const qStartDate = parse(quarter.startDate, 'yyyy-MM-dd', new Date());
      const qEndDate = parse(quarter.endDate, 'yyyy-MM-dd', new Date());
      totalDays = differenceInDays(qEndDate, qStartDate);
    } else {
      const matchedMonth1 = getMatchedMonth(totalStepsCaptured.data, month, startValue, endValue);
      const matchedMonth2 = getMatchedMonth(totalUsersSyncingSteps.data, month, startValue, endValue);

      if (matchedMonth1 && matchedMonth2) {
        totalDays = Math.min(new Date(matchedMonth1).getDate(), new Date(matchedMonth2).getDate());
        totalSteps = totalStepsCaptured.data[matchedMonth1]?.total_steps;
        totalUsers = totalUsersSyncingSteps.data[matchedMonth2]?.total_users_syncing_steps;
      }
    }
    const result = totalSteps / (totalUsers * totalDays) || 0;
    return result.toFixed(2);
  }

  function getPercentOfActiveUsersWhoSyncSteps(month) {
    if (
      totalUsersSyncingSteps.loading &&
      ((new Date(month) >= new Date(payload.startDate) &&
        new Date(month) <= new Date(payload.endDate)) ||
        isPartialMatch(month, payload.endDate))
    ) {
      return 'loading';
    }
    if (!totalUsersSyncingSteps.data || !activeUsers.data) {
      return 0;
    }
    const matchedMonth1 = getMatchedMonth(totalUsersSyncingSteps.data, month, startValue, endValue);
    const matchedMonth2 = getMatchedMonth(activeUsers.data, month, startValue, endValue);
    if (matchedMonth1 && matchedMonth2) {
      const tmpTotalUsersSyncingSteps =
        totalUsersSyncingSteps.data[matchedMonth1]?.total_users_syncing_steps;
      const realActiveUsers = activeUsers.data[matchedMonth2]?.activeUsers;
      const result = (tmpTotalUsersSyncingSteps * 100) / realActiveUsers || 0;
      return result.toFixed(2);
    }
    return 0;
  }

  function getStepsToMinsConversion(month, quarter, quarterly = false) {
    const getAvgStepsPerUserPerDayResult = getAvgStepsPerUserPerDay(month, quarter, quarterly);
    if (isNaN(getAvgStepsPerUserPerDayResult)) {
      return 0;
    }
    return (getAvgStepsPerUserPerDayResult / 75).toFixed(2);
  }

  function getAvgWaterIntakePerUserPerDay(month, quarter, quarterly = false) {
    if (
      numberOfUsersTakingWaterIntake.loading &&
      ((new Date(month) >= new Date(payload.startDate) &&
        new Date(month) <= new Date(payload.endDate)) ||
        isPartialMatch(month, payload.endDate))
    ) {
      return 'loading';
    }
    if (!numberOfUsersTakingWaterIntake.data) {
      return 0;
    }
    let tempUserCount = 0;
    let tempTotalWaterIntake = 0;
    let totalDays = 0;

    if (quarterly) {
      tempUserCount =
        Object.values(numberOfUsersTakingWaterIntake.data).find(
          (item) => item.quarter === quarter.quarterNo
        )?.total_users_taking_waterintake ?? 0;
      tempTotalWaterIntake =
        Object.values(numberOfUsersTakingWaterIntake.data).find(
          (item) => item.quarter === quarter.quarterNo
        )?.total_water_intake ?? 0;

      const qStartDate = parse(quarter.startDate, 'yyyy-MM-dd', new Date());
      const qEndDate = parse(quarter.endDate, 'yyyy-MM-dd', new Date());
      totalDays = differenceInDays(qEndDate, qStartDate);
    } else {
      const matchedMonth = getMatchedMonth(
        numberOfUsersTakingWaterIntake.data,
        month,
        startValue,
        endValue
      );
      tempUserCount =
        numberOfUsersTakingWaterIntake.data[matchedMonth]?.total_users_taking_waterintake;
      tempTotalWaterIntake = numberOfUsersTakingWaterIntake.data[matchedMonth]?.total_water_intake;
      totalDays = new Date(matchedMonth).getDate();
    }
    const result = tempTotalWaterIntake / (tempUserCount * totalDays);
    if (isNaN(result)) {
      return '-';
    }
    return result.toFixed(2);
  }

  function getPercentageOfPushRead(month) {
    if (
      totalPushSent.loading &&
      ((new Date(month) >= new Date(payload.startDate) &&
        new Date(month) <= new Date(payload.endDate)) ||
        isPartialMatch(month, payload.endDate))
    ) {
      return 'loading';
    }
    if (!totalPushSent.data) {
      return 0;
    }
    const matchedMonth = getMatchedMonth(totalPushSent.data, month, startValue, endValue);
    const tempTotalPushSent = totalPushSent.data[matchedMonth]?.total_push_sent;
    const tempTotalPushRead = totalPushSent.data[matchedMonth]?.total_push_read;
    if (tempTotalPushSent && tempTotalPushRead) {
      return ((tempTotalPushRead * 100) / tempTotalPushSent).toFixed(2);
    }
    return 0;
  }

  function navigateToDetails(field) {
    return navigate(`/overview/details?field=${field}`, {
      state: {
        filterType: selectedFilter?.value ?? filterType.last12Months,
        startDate: startValue,
        endDate: endValue,
      }},
    );
  }

  const sDate = getDateFromString(startValue);
  const eDate = getDateFromString(endValue);

  // console.log(sDate, eDate);

  const monthsInRange = getMonthsInRange(sDate, eDate);
  const quarters = getQuarterEndDates(firstDateOfQuarter(sDate), eDate);

  const getDataForRows = (field) => {
    switch (field) {
      case 'registered_users':
        return paidRegisteredUsers;
      case 'activeUsers':
        return activeUsers;
      case 'total_users_played_content':
        return totalUsersPlayedContent;
      case 'total_mins':
        return totalMinsPlayed;
      case 'total_users_rated_content':
        return usersRatedTheContent;
      case 'total_users_favorite_the_content':
        return usersFavoriteTheContent;
      case 'total_steps':
        return totalStepsCaptured;
      case 'total_users_syncing_steps':
        return totalUsersSyncingSteps;
      case 'personal_coaching_bookings_completed':
        return numberOfPcCompleted;
      case 'personal_coaching_bookings':
        return numberOfPcBookings;
      case 'total_users_taking_waterintake':
        return numberOfUsersTakingWaterIntake;
      case 'total_water_intake':
        return numberOfUsersTakingWaterIntake;
      case 'total_reward_points_earned':
        return totalEarnedRewardPoints;
      case 'total_reward_points_redeemed':
        return totalRedeemedRewardPoints
      case 'total_users_earned_rewards':
        return totalUsersEarnedReward;
      case 'total_users_redeemed_rewards':
        return totalUsersRedeemedReward;
      case 'total_push_sent':
        return totalPushSent;
      case 'total_challenges_launched':
        return totalChallengesLaunched;
      case 'total_challenges_joined':
        return totalUsersJoinedInChallenge;
      case 'total_challenges_participated':
        return totalUsersParticipatedInChallenge;
      case 'programs_joined':
        return programJoined;
      case 'programs_completed':
        return programCompleted;
      case 'audio_books_redeemed':
        return bookRead;
      case 'total_meal_images_uploaded':
        return totalMealImagesUploaded;
      case 'total_ai_messages':
        return totalAiCoachMessages;
      case 'total_meals_added':
        return totalMealsAdded;
      case 'unique_nutrition_users':
        return uniqueNutritionUsers;
      default:
        return {};
    }
  };

  const getHeaderSubtitle = (month) => {
    const sDate = getDateFromString(startValue);
    const eDate = getDateFromString(endValue);

    if (startValue && isPartialMatch(month, startValue) && sDate.getDate() > 1) {
      return `${format(sDate, 'do')} - ${format(lastDayOfMonth(sDate), 'do MMM yyyy')}`;
    } else if (
      endValue &&
      isPartialMatch(month, endValue) &&
      eDate.getDate() < 31 &&
      eDate.getMonth() !== sDate.getMonth()
    ) {
      return `${format(eDate, 'do MMM yyyy')}`;
    }
    return;
  };

  return (
    <table className='data-table table table-bordered'>
      <thead>
        <tr>
          <th scope='col' className='first-column header w-25'></th>
          {showQuarterly
            ? quarters.map((item) => (
                <th
                  scope='col'
                  key={item.quarter}
                  className='header'
                  style={{ textAlign: 'center' }}
                >
                  <div className='d-flex flex-column gap-1 align-items-center'>
                    <span>{item.quarter}</span>
                    <span>
                      {'('}{format(new Date(item.endDate), 'MMMM')}{')'}
                    </span>
                  </div>
                </th>
              ))
            : monthsInRange.map((month) => (
                <th scope='col' key={month} className='header' style={{ textAlign: 'center' }}>
                  <div className='d-flex flex-column'>
                    <span>{format(new Date(month), 'MMMM')}</span>
                    {getHeaderSubtitle(month) && (
                      <span className='subtitle'>{'('}{getHeaderSubtitle(month)}{')'}</span>
                    )}
                  </div>
                </th>
              ))}
          {!showQuarterly &&
            isCurrentMonth(monthsInRange[monthsInRange.length - 1]) &&
            monthsInRange.length > 6 && (
              <th
                scope='col'
                key='projected-current-month'
                className='last-column'
                style={{ textAlign: 'center' }}
              >
                Projected Current Month
              </th>
            )}
        </tr>
      </thead>
      <tbody>
        {showQuarterly ? (
          <>
            <TableRowComponent
              showQuarterly={true}
              quarters={quarters}
              title='Average usage per day per user (includes steps, mins and water)'
              startDate={startValue}
              endDate={endValue}
              showPercentage={true}
              getPercentageOrAvg={getAvgUsePerDay}
            />
            {rowsForQuarterlyData.map((row, index) => (
              <TableRowComponent
                clickable={companySelected[0]?._id === 'ALL'}
                onClick={navigateToDetails}
                showQuarterly={true}
                quarters={quarters}
                title={row.title}
                data={getDataForRows(row.key)}
                field={row.key}
                startDate={startValue}
                endDate={endValue}
                divisor={row.key === 'total_steps' ? 1000000 : null}
              />
            ))}
          </>
        ) : (
          <>
            {rowsForMonthlyData.map((row, index) => {
              return <TableRowComponent
                key={row.key + index}
                payload={payload}
                clickable={row.clickable ?? companySelected[0]?._id === 'ALL'}
                onClick={navigateToDetails}
                title={row.title}
                months={monthsInRange}
                data={getDataForRows(row.key)}
                field={row.key}
                divisor={row.key === 'total_steps' ? 1000000 : null}
                startDate={startValue}
                endDate={endValue}
                showPercentage={row.showPercentage}
                getPercentageOrAvg={
                  row.showPercentage
                    ? row.key === 'averageUsagePerDayPerUser'
                      ? getAvgUsePerDay
                      : row.key === 'activeUserPercentage'
                      ? getActiveUsersPercentageForMonth
                      : row.key === 'avgMinutesPlayedPerDay'
                      ? getAvgMinutesPlayedPerDay
                      : row.key === 'userRatingPercentage'
                      ? getUsersPercentageRatedTheContent
                      : row.key === 'avgStepsPerUserPerDay'
                      ? getAvgStepsPerUserPerDay
                      : row.key === 'activeUserPercentageWhoSyncedSteps'
                      ? getPercentOfActiveUsersWhoSyncSteps
                      : row.key === 'stepToMinutesConversion'
                      ? getStepsToMinsConversion
                      : row.key === 'avgWaterIntake'
                      ? getAvgWaterIntakePerUserPerDay
                      : row.key === 'pushReadPercentage'
                      ? getPercentageOfPushRead
                      : () => {}
                    : () => {}
                }
              />
            })}
          </>
        )}
      </tbody>
    </table>
  );
}

export default Last12MonthsContainer;
